import { useEffect, useMemo } from "react";
import {
  Container,
  SimpleGrid,
  Paper,
  Group,
  Stack,
  Alert,
  SegmentedControl,
  Text,
  Title,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useSearch, useLocation } from "wouter";
import { useTrafficSummaryStore } from "./trackingStore";
import { useBrandContext } from "../Brands/brandContext";
import TrafficTimeline from "./TrafficTimeline";
import TopPages from "./TopPages";
import ActiveTimes from "./ActiveTimes";
import EventTypes from "./EventTypes";
import TrafficSources from "./TrafficSources";
import TrafficLocations from "./TrafficLocations";
import SourceDetailDrawer from "./SourceDetailDrawer";
import { CHANNELS } from "./trafficDisplay";

/**
 * Periods, each with the bucket width that suits it. A 24-hour window bucketed by day is one bar;
 * ninety days bucketed by hour is 2,160 of them.
 */
const PERIODS = {
  "1": { label: "24 hours", days: 1, granularity: "hour" },
  "7": { label: "7 days", days: 7, granularity: "day" },
  "30": { label: "30 days", days: 30, granularity: "day" },
  "90": { label: "90 days", days: 90, granularity: "week" },
};

const DEFAULT_PERIOD = "30";

export default function AnalyticsDashboard() {
  const { selectedBrand } = useBrandContext();
  const [, setLocation] = useLocation();
  const searchString = useSearch();

  // The period lives in the URL so the page is linkable and the browser Back button works —
  // metric tiles elsewhere can now deep-link straight to the window they describe.
  const { period, channel } = useMemo(() => {
    const params = new URLSearchParams(searchString);
    const requested = params.get("period");
    const requestedChannel = params.get("channel");
    return {
      period: PERIODS[requested] ? requested : DEFAULT_PERIOD,
      channel: CHANNELS.includes(requestedChannel) ? requestedChannel : null,
    };
  }, [searchString]);

  const go = (next) => {
    const params = new URLSearchParams();
    params.set("period", next.period ?? period);
    const nextChannel = next.channel === undefined ? channel : next.channel;
    if (nextChannel) params.set("channel", nextChannel);
    setLocation(`/web-traffic?${params}`);
  };

  const { days, granularity } = PERIODS[period];
  const { summary, isLoading, error, fetchSummary, hasSummaryFor, hasFailedFor } =
    useTrafficSummaryStore();

  useEffect(() => {
    if (!selectedBrand?.id) return;
    fetchSummary(selectedBrand.id, { days, granularity });
  }, [selectedBrand?.id, days, granularity]);

  if (!selectedBrand) {
    return (
      <Container size="xl" py="md">
        <Alert icon={<IconAlertCircle size={16} />} title="No Brand Selected" color="yellow">
          Please select a brand from the top navigation to view analytics.
        </Alert>
      </Container>
    );
  }

  // Only paint numbers that answer the question currently on screen. Without this the previous
  // brand's or the previous window's figures show through while the new request is in flight.
  const isCurrent = hasSummaryFor(selectedBrand.id, { days, granularity });
  const failed = hasFailedFor(selectedBrand.id, { days, granularity });
  const data = isCurrent ? summary : null;
  // A failed request is not a slow one. Without this branch the page sat on "Loading..." forever
  // with nothing anywhere saying the request had died.
  const loading = !failed && (isLoading || !isCurrent);

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Paper p="md" withBorder>
          <Group justify="space-between" wrap="wrap">
            <Group gap="lg">
              <Title order={4}>Web traffic</Title>
              {data?.totals && (
                <Group gap="lg">
                  <Total label="Pageviews" value={data.totals.pageviews} />
                  <Total label="Sessions" value={data.totals.sessions} />
                  <Total label="Visitors" value={data.totals.visitors} />
                </Group>
              )}
            </Group>
            <SegmentedControl
              size="xs"
              value={period}
              onChange={(next) => go({ period: next })}
              data={Object.entries(PERIODS).map(([value, p]) => ({ value, label: p.label }))}
            />
          </Group>
        </Paper>

        {failed && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" title="Couldn't load traffic">
            {error ?? "Something went wrong."} Try another period, or reload the page.
          </Alert>
        )}

        <SimpleGrid cols={1} spacing="md">
          <TrafficTimeline
            series={data?.series}
            granularity={data?.granularity}
            isLoading={loading}
          />
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <TrafficSources
              sources={data?.sources}
              origins={data?.origins}
              isLoading={loading}
              onSelectChannel={(next) => go({ channel: next })}
            />
            <TrafficLocations
              countries={data?.countries}
              cities={data?.cities}
              totalSessions={data?.totals?.sessions}
              isLoading={loading}
            />
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <TopPages pages={data?.topPages} isLoading={loading} />
            <EventTypes byEventName={data?.byEventName} isLoading={loading} />
          </SimpleGrid>
          <ActiveTimes byHour={data?.byHour} isLoading={loading} />
        </SimpleGrid>

        <SourceDetailDrawer
          opened={!!channel}
          onClose={() => go({ channel: null })}
          channel={channel}
          sessions={data?.sources?.find((s) => s.source === channel)?.sessions}
          origins={data?.origins}
          campaigns={data?.campaigns}
          media={data?.media}
          terms={data?.terms}
          countries={data?.countries}
          cities={data?.cities}
          landings={data?.landings}
        />

        <Text size="xs" c="dimmed">
          Counted in UTC over the whole period. Visitors are counted per day and cannot be joined
          across days by design, so the visitor figure is distinct people within the window as far
          as a cookieless measure can tell.
        </Text>
      </Stack>
    </Container>
  );
}

function Total({ label, value }) {
  return (
    <Group gap={6} align="baseline">
      <Text size="lg" fw={700}>{(value ?? 0).toLocaleString()}</Text>
      <Text size="xs" c="dimmed">{label}</Text>
    </Group>
  );
}
